import { useIonModal } from '@ionic/react'
import { AnimatePresence, motion } from 'framer-motion'

import { QUERY_GET_PORTAL_NOTES, useGetPortalNotes } from '@apps-common/apis/portal/portal-note'
import { NoResultsAddCard } from '@apps-common/components/common'
import { SectionTitle } from '@apps-common/components/common/portal-title'
import { ButtonRoundArrow, ButtonRoundLg } from '@apps-common/components/elements'
import { ModalButton } from '@apps-common/components/modals'
import { commonIcons } from '@apps-common/config/icons'
import { motionFadeIn, motionSlideDownFadeIn } from '@apps-common/config/motion-variants'
import { useNavigation } from '@apps-common/hooks/useNavigation'
import { queryClient } from '@apps-common/lib'
import { modalSlideUpEnterAnimation, modalSlideUpLeaveAnimation } from '@apps-common/lib/modal-animations'
import { ROUTE_UNIT_NOTES } from '@apps-radionet-portal-root/src/config/private-routes'

import { PortalNoteModal } from '../../pages/notes/components'

import { NoteItemCard } from './NoteItemCard'
import { NoteItemSkeletonCard } from './NoteItemSkeletonCard'

interface IProps {
  status: string
  unitId: string
  classes?: string
}

export const NotesCard: React.FC<IProps> = ({ status, unitId }) => {
  const { navigateForward } = useNavigation()
  const {
    isLoading: notesIsLoading,
    isError: notesIsError,
    data: notesData
  } = useGetPortalNotes({ config: { enabled: !!unitId }, entityId: unitId })

  const [present, dismiss] = useIonModal(PortalNoteModal, {
    componentProps: {
      entityId: unitId
    },
    onDismiss: (data: string, role: string) => {
      queryClient.invalidateQueries({ queryKey: QUERY_GET_PORTAL_NOTES() })
      dismiss(data, role)
    },
    enableBackdropDismiss: true
  })

  const navigateNotes = () => navigateForward({ routeUrl: ROUTE_UNIT_NOTES.buildPath(status, unitId), route: ROUTE_UNIT_NOTES })

  const remaining = (notesData?.length || 0) - 1
  const pinnedNote = notesData?.find((d) => d.pinned)

  const loading = !unitId || notesIsLoading || notesIsError

  return (
    <div className='relative sm:bg-surface sm:border border-border m-0 h-full p-0 sm:p-[12px] rounded-[30px] sm:overflow-hidden flex flex-col gap-[10px]'>
      {/* <div className='hidden sm:block absolute bg-surface-2 top-0 left-0 w-full h-full' /> */}
      <div className='hidden sm:flex z-[2] items-center justify-between'>
        <SectionTitle classes='text-text ml-[10px]' text='Notes' />
        <div className='flex items-center justify-end gap-[5px]'>
          <ModalButton
            component={PortalNoteModal}
            componentProps={{
              entityId: unitId
            }}
            enterAnimation={modalSlideUpEnterAnimation}
            leaveAnimation={modalSlideUpLeaveAnimation}
          >
            <ButtonRoundLg type='div' aria-label='Add note' className='action-success'>
              <commonIcons.add className='text-[1.2rem]' />
            </ButtonRoundLg>
          </ModalButton>
          <ButtonRoundArrow classes='bg-brand hover:bg-brand' iconClasses='text-on-brand' callback={navigateNotes} />
        </div>
      </div>
      <AnimatePresence>
        <div key='unit-manage-desktop-notes' className='hidden sm:flex grow flex-col gap-[10px]'>
          {!loading && notesData?.length === 0 && (
            <NoResultsAddCard
              title='No notes'
              message='Add a new note'
              disabled={loading}
              callback={() => present()}
              classes1='!bg-surface-2'
              classes2='!bg-surface'
              classes3='!bg-surface-2'
            />
          )}
          {loading && (
            <>
              <NoteItemSkeletonCard key='unit-manage-note-card-skeleton' />

              <div className='grow flex flex-col items-center justify-center relative z-1'>
                <div className='grow mx-auto bg-surface-2 text-text !text-[0.8rem] !h-auto !max-h-[35px] !min-h-[24px] !min-w-[90px] !px-[20px] rounded-full flex items-center justify-between gap-[5px]' />
              </div>
            </>
          )}
          {!loading && (
            <>
              {notesData?.slice(0, 1).map((d, i) => (
                <NoteItemCard key={`unit-manage-note-card-${d.portalNoteId}-${i}`} note={d} classes='!bg-surface-2' />
              ))}
              <button
                type='button'
                className='absolute top-[-12px] right-[8px] sm:hidden bg-surface text-text rounded-full flex items-center justify-center overflow-hidden'
                onClick={navigateNotes}
              >
                <div className='absolute z-0 bg-surface w-full h-full' />
                <div className='relative flex items-center justify-between gap-[10px] pl-[10px] pr-[2px] py-[2px] text-[0.9rem]'>
                  Notes
                  <div className='border-[1px] border-primary-content rounded-full p-[2px]'>
                    <commonIcons.arrowAngled />
                  </div>
                </div>
              </button>
              <div className='grow hidden sm:flex flex-col items-center justify-center relative z-1'>
                <button
                  type='button'
                  className='grow mx-auto bg-surface-2 text-text sm:text-text border border-border !text-[0.8rem] !h-auto !max-h-[35px] !min-h-[24px] !min-w-0 !px-[20px] rounded-full flex items-center justify-between gap-[5px]'
                  onClick={navigateNotes}
                >
                  {remaining > 0 && <div>+{remaining}</div>}
                  More notes {remaining <= 0 && <commonIcons.arrowAngled className='text-[1.1em]' />}
                </button>
              </div>
            </>
          )}
        </div>
        <div key='unit-manage-mobile-notes' className='block sm:hidden'>
          {pinnedNote && (
            <motion.div key='mobile-unit-manage-pinned-note' layout {...motionSlideDownFadeIn(0.35)}>
              <NoteItemCard
                key={`unit-manage-note-card-${pinnedNote.portalNoteId}`}
                note={pinnedNote}
                classes='!bg-surface-2 mt-4 mb-[7px]'
              />
              <motion.div
                key='mobile-unit-manage-pinned-note-all-notes'
                {...motionFadeIn(0.2, 0.4)}
                className='absolute bottom-[-14px] left-0 right-0 sm:hidden flex items-center justify-center overflow-hidden'
              >
                <div className='bg-surface text-text border border-border rounded-full'>
                  <button
                    type='button'
                    className='rounded-full flex items-center justify-center gap-[5px] text-[0.8rem] jost-light px-[10px] py-[2px]'
                    onClick={(e) => {
                      e?.stopPropagation()
                      navigateNotes()
                    }}
                  >
                    More notes
                    <commonIcons.arrowAngled />
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </div>
      </AnimatePresence>
    </div>
  )
}
